// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Autocomplete } from '@mui/material';
import { AppIcon, Heading } from '@shared/ui-kit/components';
import { type FC, type PropsWithChildren, useCallback, useState } from 'react';
import { type TagsSuggestions } from '../../types';
import { useTranslation } from 'react-i18next';

interface TagManagerEditorProps extends PropsWithChildren {
  onTag: (
    tag: string,
    includeTickers: boolean,
    addDraftTag: (tag: string, tickers: string[]) => Promise<void>,
    selectedSuggestion: TagsSuggestions,
  ) => void;
  suggestions: TagsSuggestions[];
  addDraftTag: (tag: string, tickers: string[]) => Promise<void>;
}

export const TagManagerEditor: FC<TagManagerEditorProps> = ({
  onTag,
  children,
  suggestions,
  addDraftTag,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<TagsSuggestions | null>(null);

  const onButtonClick = useCallback(() => {
    onTag(text, !!selectedSuggestion, addDraftTag, selectedSuggestion!);

    setText('');
    setSelectedSuggestion(null);
  }, [onTag, text, selectedSuggestion, addDraftTag]);

  return (
    <div className="flex flex-col space-y-9 pb-8">
      <div className="w-full">
        <div className="grid grid-cols-4 gap-2">
          <div className="col-span-1">
            <Heading
              variant="h2"
              title={t('tag-manager.title', { ns: 'components' })}
            />
          </div>
          <div className="col-span-1 pr-2">
            <Autocomplete
              freeSolo
              id="autocomplete-tag"
              disableClearable
              options={suggestions}
              groupBy={option => option.feature}
              getOptionLabel={option =>
                /* istanbul ignore next */
                typeof option === 'string' ? option : option.tagName
              }
              onChange={
                /* istanbul ignore next */ (_, value) => {
                  if (typeof value === 'object') {
                    setText(value?.tagName);
                    setSelectedSuggestion(value);
                  }
                }
              }
              onInputChange={(_, value) => {
                setText(value);
                setSelectedSuggestion(null);
              }}
              value={text}
              renderInput={params => (
                <div
                  className="input-ticker relative"
                  data-testid="search-tag"
                  ref={params.InputProps.ref}
                >
                  <input
                    {...params.inputProps}
                    className="input-rounded"
                    name="search_text"
                    placeholder={t('tag-manager.add-tag', { ns: 'components' })}
                    required
                    type="text"
                    autoComplete="off"
                  />
                  <div className="absolute right-3 top-1.5">
                    <button
                      type="submit"
                      data-testid="create-global-tag-button"
                      className="inline-flex h-7 w-7 items-center justify-center hover:cursor-pointer"
                      disabled={!params.inputProps.value}
                      onClick={onButtonClick}
                    >
                      <AppIcon
                        icon="Add"
                        size="lg"
                        color="disabled"
                        className="hover:!text-primary"
                      />
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </div>
      <div
        className="flex flex-wrap items-center gap-x-6 gap-y-3.5 text-white"
        data-testid="tag-manager-editor-child"
      >
        {children}
      </div>
    </div>
  );
};
